import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiImage, FiX, FiCheck, FiLoader, FiTrash2, FiGrid } from 'react-icons/fi';
import { mediaAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * ImageUploader - Component for uploading images instead of using URLs
 * Supports drag & drop, file selection, and media library browsing
 */
export default function ImageUploader({ 
  value, 
  onChange, 
  label = "Image",
  category = "general",
  showLibrary = true,
  className = ""
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non supporté. Utilisez JPG, PNG, GIF, WebP ou SVG.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux. Maximum 10MB.');
      return;
    }

    setIsUploading(true);
    try {
      const response = await mediaAPI.upload(file, category, true);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      onChange(imageUrl);
      toast.success('Image uploadée avec succès !');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const loadMediaLibrary = async () => {
    setLoadingLibrary(true);
    try {
      // First try to load with the specific category
      let response = await mediaAPI.getAll(category);
      let mediaData = response.data?.media || response.data || [];
      
      // If no media found with specific category, load all media
      if (!Array.isArray(mediaData) || mediaData.length === 0) {
        response = await mediaAPI.getAll(); // Load all media
        mediaData = response.data?.media || response.data || [];
      }
      
      setMediaLibrary(Array.isArray(mediaData) ? mediaData : []);
    } catch (error) {
      console.error('Error loading media library:', error);
      setMediaLibrary([]);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const openMediaLibrary = () => {
    setShowMediaLibrary(true);
    loadMediaLibrary();
  };

  const selectFromLibrary = (media) => {
    // Check if URL is already absolute (starts with http:// or https://)
    const imageUrl = media.url.startsWith('http://') || media.url.startsWith('https://') 
      ? media.url 
      : `${process.env.REACT_APP_BACKEND_URL}${media.url}`;
    onChange(imageUrl);
    setShowMediaLibrary(false);
    toast.success('Image sélectionnée !');
  };

  const clearImage = () => {
    onChange('');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <label className="text-sm font-medium text-slate-700">{label}</label>}
      
      {/* Current Image Preview */}
      {value && (
        <div className="relative group">
          <img 
            src={value} 
            alt="Preview" 
            className="w-full h-32 object-cover rounded-lg border border-slate-200"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="p-2 bg-white rounded-full hover:bg-slate-100 transition-colors"
              title="Remplacer"
            >
              <FiUpload className="h-4 w-4 text-slate-700" />
            </button>
            <button
              onClick={clearImage}
              className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
              title="Supprimer"
            >
              <FiTrash2 className="h-4 w-4 text-red-500" />
            </button>
          </div>
        </div>
      )}

      {/* Upload Zone */}
      {!value && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer
            ${isDragging ? 'border-violet-500 bg-violet-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}
            ${isUploading ? 'pointer-events-none opacity-60' : ''}
          `}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <FiLoader className="h-8 w-8 text-violet-500 animate-spin mb-2" />
              <p className="text-sm text-slate-600">Upload en cours...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <FiUpload className="h-8 w-8 text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 mb-1">
                Glissez une image ici ou cliquez pour sélectionner
              </p>
              <p className="text-xs text-slate-400">
                JPG, PNG, GIF, WebP, SVG • Max 10MB
              </p>
            </div>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e.target.files[0])}
        className="hidden"
      />

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 transition-colors"
        >
          <FiUpload className="h-4 w-4" />
          Uploader
        </button>
        {showLibrary && (
          <button
            onClick={openMediaLibrary}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <FiGrid className="h-4 w-4" />
            Médiathèque
          </button>
        )}
      </div>

      {/* Media Library Modal */}
      <AnimatePresence>
        {showMediaLibrary && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setShowMediaLibrary(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Médiathèque</h3>
                <button
                  onClick={() => setShowMediaLibrary(false)}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                {loadingLibrary ? (
                  <div className="flex items-center justify-center py-12">
                    <FiLoader className="h-8 w-8 text-violet-500 animate-spin" />
                  </div>
                ) : mediaLibrary.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FiImage className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Aucune image dans la médiathèque</p>
                    <p className="text-sm mt-1">Uploadez des images pour les voir ici</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-4">
                    {mediaLibrary.map((media) => (
                      <div
                        key={media.id}
                        onClick={() => selectFromLibrary(media)}
                        className="relative aspect-square cursor-pointer group rounded-lg overflow-hidden border-2 border-transparent hover:border-violet-500 transition-all"
                      >
                        <img
                          src={media.url.startsWith('http://') || media.url.startsWith('https://') ? media.url : `${process.env.REACT_APP_BACKEND_URL}${media.url}`}
                          alt={media.alt_text || media.original_filename}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <FiCheck className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
